import { Fragment, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { postJSON, fetchJSON } from '../api/client';
import { getS3Projects, refreshS3Projects, loadS3Project, analyzeNewFiles, pmListProjects } from '../api/endpoints';
import type { S3ProjectCard, AssignmentCounts, AnnotatorProgress } from '../api/endpoints';
import { useProjectStore } from '../store/useProjectStore';
import { useSSEStore } from '../store/useSSEStore';
import { useAuthStore } from '../store/useAuthStore';

interface ProjectEntry {
  project_id: string;
  name: string;
  source_dir: string;
  s3_uri: string | null;
  created_at: string;
  skill_count: number;
  running: boolean;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Completed', cls: 'completed' },
  extracting: { label: 'Extracting', cls: 'in-progress' },
  annotating: { label: 'Annotating', cls: 'in-progress' },
  research: { label: 'Research', cls: 'research' },
  triaged: { label: 'Triaged', cls: 'triaged' },
  uploaded: { label: 'Uploaded', cls: 'new' },
  in_progress: { label: 'In Progress', cls: 'in-progress' },
  new: { label: 'New', cls: 'new' },
};

export function HomeScreen() {
  const navigate = useNavigate();
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner';
  // Open a project by routing to its URL (no reload). ProjectView resolves
  // the default tab and shows a loader while the project loads.
  const open = (projectId: string) => navigate(`/p/${encodeURIComponent(projectId)}`);
  const [activeProjectId, setActiveProjectId] = useState<string | undefined>(
    useProjectStore.getState().project?.project_id
  );
  const [sourcePath, setSourcePath] = useState('');
  const [flags, setFlags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localProjects, setLocalProjects] = useState<ProjectEntry[]>([]);
  const [s3Cards, setS3Cards] = useState<S3ProjectCard[]>([]);
  const [s3Loading, setS3Loading] = useState(false);
  // False until the first fetch resolves — so we show a loader instead of
  // the "no projects" empty state before any data has arrived.
  const [s3Fetched, setS3Fetched] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(false);
  const [accessibleIds, setAccessibleIds] = useState<Set<string> | null>(null);
  // "Open work only" filter, remembered per browser (a convenience only).
  const [openOnly, setOpenOnlyState] = useState<boolean>(() => {
    try { return localStorage.getItem('projects.openOnly') === '1'; } catch { return false; }
  });
  const setOpenOnly = (v: boolean) => {
    setOpenOnlyState(v);
    try { localStorage.setItem('projects.openOnly', v ? '1' : '0'); } catch { /* storage unavailable */ }
  };
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // For annotators: fetch the set of project IDs they have access to
  useEffect(() => {
    if (isOwner) return; // owners see everything
    pmListProjects()
      .then((projects) => {
        if (projects) {
          setAccessibleIds(new Set(projects.map((p) => p.id)));
        }
      })
      .catch(() => setAccessibleIds(new Set()));
  }, [isOwner]);

  // Landing on the home screen — clear any in-memory project so the
  // dashboard isn't tied to a previously-opened one. (Project identity
  // lives only in the URL now; there's no server-side active project.)
  useEffect(() => {
    useProjectStore.getState().setProject(null);
    setActiveProjectId(undefined);
  }, []);

  // Fetch local projects
  useEffect(() => {
    fetchJSON<ProjectEntry[]>('/api/projects')
      .then(data => { if (data) setLocalProjects(data); })
      .catch(() => {});
  }, []);

  // Fetch projects from S3 (cached, background-refreshed by the server)
  const fetchS3 = useCallback(async () => {
    const result = await getS3Projects();
    setS3Fetched(true);
    if (result) {
      setS3Cards(result.projects);
      setS3Loading(result.loading);
      return result.loading;
    }
    return false;
  }, []);

  useEffect(() => {
    fetchS3();
  }, [fetchS3]);

  // Poll while S3 metadata is still loading
  useEffect(() => {
    if (!s3Loading) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      const stillLoading = await fetchS3();
      if (!stillLoading && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [s3Loading, fetchS3]);

  const handleStart = async (path?: string) => {
    const src = (path || sourcePath).trim();
    if (!src) return;
    setLoading(true);
    setError('');
    try {
      const raw = await fetch('/api/survey/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_path: src, flags }),
      });
      const body = await raw.json().catch(() => ({}));
      if (!raw.ok) throw new Error(body.error || `Server error ${raw.status}`);
      // Redirect straight to the project (the loader + live SSE take over).
      if (body.project_id) open(body.project_id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  };

  const handleRunSurvey = async (e: React.MouseEvent, card: S3ProjectCard) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/survey/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3_uri: card.s3_uri, flags }),
      });
      // 409 = a pipeline is already running for this project. That's not an
      // error here — just open it so the user can watch the existing run.
      if (res.status === 409) {
        open(card.project_id);
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Server error ${res.status}`);

      // Redirect to the project page immediately — the loader + live SSE
      // show the run as it progresses.
      open(card.project_id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  };

  const handleProcessNewFiles = async (e: React.MouseEvent, card: S3ProjectCard) => {
    e.stopPropagation();
    if (loading) return;
    const n = card.new_files.length;
    const ok = window.confirm(
      `${n} new file${n === 1 ? '' : 's'} detected:\n\n${card.new_files.join('\n')}\n\n` +
      `Analyze just these new files and merge into this project? ` +
      `Existing annotations and extractions are kept — nothing is deleted.`
    );
    if (!ok) return;
    setLoading(true);
    setError('');
    try {
      const res = await analyzeNewFiles(card.s3_uri);
      if (!res) throw new Error('Failed to start analysis');
      open(res.project_id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  };

  const handleSelect = async (projectId: string) => {
    setLoading(true);
    setError('');
    try {
      // Register the project + grant access (so the /p/{id} access check
      // passes), then route to it.
      await postJSON('/api/survey/select', { project_id: projectId });
      open(projectId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  };

  const handleS3CardClick = (card: S3ProjectCard) => {
    if (loading) return;

    // We already know the project id — route to it immediately so the
    // loader shows on the project page, not here. For non-running projects
    // kick off the S3 sync in the background (it registers access + grants
    // it, then downloads results); ProjectView briefly retries init to
    // absorb the moment before access is granted. A running project is
    // never re-synced (that could clobber the live run's local state).
    if (!card.running) {
      loadS3Project(card.s3_uri).catch(() => {});
    }
    open(card.project_id);
  };

  const handleRefresh = async () => {
    setS3Loading(true);
    await refreshS3Projects();
    // Small delay then re-fetch
    setTimeout(() => fetchS3(), 500);
  };

  const handleDelete = async (e: React.MouseEvent, p: ProjectEntry) => {
    e.stopPropagation();
    if (!confirm(`Delete project "${p.name}"?\n\nThis will permanently remove all results.`)) return;
    try {
      const raw = await fetch('/api/survey/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: p.project_id }),
      });
      const body = await raw.json().catch(() => ({}));
      if (!raw.ok) throw new Error(body.error || `Server error ${raw.status}`);
      setLocalProjects(prev => prev.filter(x => x.project_id !== p.project_id));
      if (activeProjectId === p.project_id) setActiveProjectId(undefined);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleFlag = (flag: string) => {
    setFlags(prev => prev.includes(flag) ? prev.filter(f => f !== flag) : [...prev, flag]);
  };

  // Annotators only see projects they have access to
  const visibleCards = !isOwner && accessibleIds
    ? s3Cards.filter((c) => accessibleIds.has(c.project_id))
    : s3Cards;

  const openAnnotationCount = visibleCards.filter((c) => c.annotation_open).length;
  const newFilesCount = visibleCards.filter((c) => (c.new_files?.length ?? 0) > 0).length;
  const completedCount = visibleCards.filter(c => c.status === 'completed').length;
  const inProgressCount = visibleCards.filter(c => c.status === 'in_progress').length;

  return (
    <div className="dashboard">
      <div className="dashboard-inner">
        {/* Header */}
        <div className="dashboard-header">
          <h1>Monco Smart Surveyor</h1>
          <p>Electrical quantity takeoff from construction documents</p>
          {isOwner && (
            <Link to="/usage" className="dashboard-usage-link">Client overview &rarr;</Link>
          )}
        </div>

        {/* Start bar — owner only */}
        {isOwner && (
          <div className="dashboard-start-bar">
            <div className="dashboard-start-row">
              <input
                type="text"
                value={sourcePath}
                onChange={e => setSourcePath(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleStart()}
                placeholder="s3://bucket/project/  or  /local/path/to/documents"
                disabled={loading}
                className="dashboard-input"
              />
              <button
                onClick={() => handleStart()}
                disabled={loading || !sourcePath.trim()}
                className="dashboard-start-btn"
              >
                {loading ? 'Starting...' : 'Start Survey'}
              </button>
            </div>
            <div className="dashboard-flags">
              {(['--no-agent', '--research-only', '--skip-extraction'] as const).map(flag => (
                <label key={flag} className="dashboard-flag-label">
                  <input
                    type="checkbox"
                    checked={flags.includes(flag)}
                    onChange={() => toggleFlag(flag)}
                    disabled={loading}
                  />
                  {flag}
                </label>
              ))}
            </div>
            {error && <div className="dashboard-error">{error}</div>}
          </div>
        )}
        {!isOwner && error && <div className="dashboard-error">{error}</div>}

        {/* S3 Projects Grid */}
        <div className="dashboard-section">
          <div className="dashboard-section-header">
            <h2>
              Projects
              {visibleCards.length > 0 && <span className="dashboard-count">({visibleCards.length})</span>}
              {visibleCards.length > 0 && (
                <span className="dashboard-stats">
                  {completedCount} completed, {inProgressCount} in progress
                </span>
              )}
            </h2>
            {visibleCards.length > 0 && (
              <div className="project-legend">
                <span><i className="project-legend-mark open-annotation" />annotation not done ({openAnnotationCount})</span>
                <span><i className="project-legend-mark has-new-files" />new files not analyzed ({newFilesCount})</span>
                <label>
                  <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
                  Open work only
                </label>
              </div>
            )}
            <button
              onClick={handleRefresh}
              disabled={s3Loading}
              className="dashboard-refresh-btn"
              title="Refresh from S3"
            >
              {s3Loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {visibleCards.length === 0 && (!s3Fetched || s3Loading) && (
            <div className="project-table-loading">Loading projects...</div>
          )}

          {visibleCards.length > 0 && (
            <ProjectTable
              cards={openOnly ? visibleCards.filter(isOpenWork) : visibleCards}
              loading={loading}
              isOwner={isOwner}
              onCardClick={handleS3CardClick}
              onRunSurvey={handleRunSurvey}
              onProcessNewFiles={handleProcessNewFiles}
            />
          )}

          {visibleCards.length === 0 && s3Fetched && !s3Loading && (
            <div className="dashboard-empty">
              No S3 projects found. Start a new survey above or check your S3_PROJECTS_URI configuration.
            </div>
          )}
        </div>

        {/* Local Projects (collapsible) */}
        {localProjects.length > 0 && (
          <div className="dashboard-section dashboard-local">
            <div
              className="dashboard-section-header clickable"
              onClick={() => setLocalExpanded(!localExpanded)}
            >
              <h2>
                {localExpanded ? '\u25BE' : '\u25B8'} Local Projects
                <span className="dashboard-count">({localProjects.length})</span>
              </h2>
            </div>
            {localExpanded && (
              <div className="dashboard-local-list">
                {localProjects.map(p => (
                  <div
                    key={p.project_id}
                    className={`dashboard-local-item ${p.project_id === activeProjectId ? 'active' : ''}`}
                    onClick={() => !loading && handleSelect(p.project_id)}
                  >
                    <div className="dashboard-local-info">
                      <span className="dashboard-local-name">{p.name}</span>
                      <span className="dashboard-local-path">{p.s3_uri || p.source_dir}</span>
                    </div>
                    <div className="dashboard-local-meta">
                      {p.running && <span className="dashboard-local-running">Running</span>}
                      <span>{p.skill_count} results</span>
                      {isOwner && (
                        <button
                          onClick={e => handleDelete(e, p)}
                          className="dashboard-local-delete"
                          title="Delete project"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ── Project Table ─────────────────────────────────────────── */

// Monco's own project status, as its app words it.
const MONCO_STATUS: Record<string, string> = {
  created: 'created', in_process: 'in process', in_validation: 'in validation', done: 'done',
};

function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 45) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Work still to do on a project: annotation not finished, or files the
// agent has not analyzed yet.
function isOpenWork(c: S3ProjectCard): boolean {
  return Boolean(c.annotation_open || (c.new_files && c.new_files.length));
}

// "3h ago" precision for recent file activity.
function fmtAgoFine(iso: string | null | undefined): string {
  if (!iso) return '-';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return fmtAgo(iso);
}

// Landed time; with the date when it is not the day the row is grouped under.
function fmtLanded(landed: string | null | undefined, groupedBy: string | null | undefined): string {
  if (!landed) return '-';
  const d = new Date(landed);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = groupedBy && new Date(groupedBy).toDateString() === d.toDateString();
  return sameDay ? time : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
}

const SEGMENTS: Array<{ key: 'completed' | 'in_progress' | 'assigned' | 'skipped'; color: string; label: string }> = [
  { key: 'completed', color: 'var(--accent-success)', label: 'done' },
  { key: 'in_progress', color: 'var(--accent-warning)', label: 'in progress' },
  { key: 'assigned', color: 'var(--border-active)', label: 'not started' },
  { key: 'skipped', color: 'var(--text-secondary)', label: 'skipped' },
];

// Done / in progress / not started / skipped pages, as a bar + "12/30 done".
function AssignmentBar({ counts }: { counts: AssignmentCounts | null | undefined }) {
  if (!counts || !counts.total) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const tip = SEGMENTS.map((s) => `${counts[s.key]} ${s.label}`).join(' · ')
    + (counts.last_completed_at ? `\nlast page done ${fmtAgo(counts.last_completed_at)}` : '');
  return (
    <div title={tip} style={{ minWidth: 110 }}>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-surface)' }}>
        {SEGMENTS.map((s) => counts[s.key] > 0 && (
          <div key={s.key} style={{ width: `${(counts[s.key] / counts.total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div style={{ fontSize: 11, marginTop: 3, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        <b style={{ color: 'var(--text-primary)' }}>{counts.completed}/{counts.total}</b> done
        {counts.in_progress > 0 && <> · {counts.in_progress} in progress</>}
      </div>
    </div>
  );
}

// Compact annotator summary for the row; the breakdown opens on click so
// long lists (usernames are emails) never widen the table.
function AnnotatorToggle({ people, open, onToggle }: {
  people: AnnotatorProgress[]; open: boolean; onToggle: () => void;
}) {
  if (!people.length) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const working = people.filter((a) => a.total > 0);
  const finished = working.filter((a) => a.completed + a.skipped >= a.total).length;
  const busy = working.filter((a) => a.in_progress > 0).length;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-expanded={open}
      title="Show each annotator's pages"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
        padding: '2px 6px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
        background: open ? 'var(--bg-surface)' : 'transparent',
        border: '1px solid var(--border-active)', color: 'var(--text-primary)',
      }}
    >
      {working.length || people.length} {(working.length || people.length) === 1 ? 'person' : 'people'}
      {finished > 0 && <span style={{ color: 'var(--accent-success)' }}>✓{finished}</span>}
      {busy > 0 && <span style={{ color: 'var(--accent-warning)' }}>●{busy}</span>}
      <span style={{ color: 'var(--text-secondary)' }}>{open ? '▴' : '▾'}</span>
    </button>
  );
}

// The opened breakdown: one line per annotator.
function AnnotatorDetail({ people }: { people: AnnotatorProgress[] }) {
  const working = people.filter((a) => a.total > 0);
  const idle = people.filter((a) => a.total === 0);
  const th: React.CSSProperties = { textAlign: 'left', fontWeight: 500, padding: '4px 10px', color: 'var(--text-secondary)', fontSize: 11 };
  const td: React.CSSProperties = { padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap' };
  return (
    <div style={{ padding: '6px 4px 10px 4px' }} onClick={(e) => e.stopPropagation()}>
      {working.length > 0 && (
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Annotator</th>
              <th style={th}>Progress</th>
              <th style={{ ...th, textAlign: 'right' }}>Done</th>
              <th style={{ ...th, textAlign: 'right' }}>In progress</th>
              <th style={{ ...th, textAlign: 'right' }}>Not started</th>
              <th style={{ ...th, textAlign: 'right' }}>Skipped</th>
              <th style={th}>Started</th>
              <th style={th}>Last page done</th>
            </tr>
          </thead>
          <tbody>
            {working.map((a) => (
              <tr key={a.user_id}>
                <td style={{ ...td, color: 'var(--text-primary)' }}>{a.username}</td>
                <td style={{ ...td, minWidth: 120 }}><AssignmentBar counts={a} /></td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--accent-success)' }}>{a.completed}</td>
                <td style={{ ...td, textAlign: 'right', color: a.in_progress ? 'var(--accent-warning)' : undefined }}>{a.in_progress}</td>
                <td style={{ ...td, textAlign: 'right' }}>{a.assigned}</td>
                <td style={{ ...td, textAlign: 'right' }}>{a.skipped}</td>
                <td style={{ ...td, color: 'var(--text-secondary)' }}>{fmtAgo(a.started_at)}</td>
                <td style={{ ...td, color: 'var(--text-secondary)' }}>{fmtAgo(a.last_completed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {idle.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '6px 10px 0' }}>
          Access but no pages assigned: {idle.map((a) => a.username).join(', ')}
        </div>
      )}
    </div>
  );
}

// Status, folder, name, org, type, files, landed, annotation, annotators, actions.
const TABLE_COLUMNS = ['9%', '5%', '16%', '12%', '7%', '5%', '11%', '11%', '12%', '12%'];

function ProjectTable({ cards, loading, isOwner, onCardClick, onRunSurvey, onProcessNewFiles }: {
  cards: S3ProjectCard[];
  loading: boolean;
  isOwner: boolean;
  onCardClick: (card: S3ProjectCard) => void;
  onRunSurvey: (e: React.MouseEvent, card: S3ProjectCard) => void;
  onProcessNewFiles: (e: React.MouseEvent, card: S3ProjectCard) => void;
}) {
  // Group by date
  const grouped = useMemo(() => {
    // Deterministic, stable order so the list doesn't reshuffle while the
    // backend metadata is still loading (the poll re-fetches every 2s).
    // Primary: created_at desc; tiebreak by the S3 folder's numeric id
    // desc, then the raw folder/id — fully determined by the data, so any
    // snapshot sorts identically regardless of backend response order.
    const folderNum = (c: S3ProjectCard) => {
      const n = parseInt(c.s3_folder || '', 10);
      return Number.isNaN(n) ? -1 : n;
    };
    // Newest file added first: a project that just got a new file jumps to
    // the top. last_file_at = newest of the S3 source file, the last Monco
    // upload and the landing date (all known before S3 metadata finishes).
    const ts = (c: S3ProjectCard) => {
      const v = c.last_file_at || c.landed_at || c.created_at;
      return v ? new Date(v).getTime() : 0;
    };
    const sorted = [...cards].sort((a, b) => {
      const da = ts(a);
      const db = ts(b);
      if (db !== da) return db - da;               // newest first
      const fb = folderNum(b), fa = folderNum(a);
      if (fb !== fa) return fb - fa;               // higher folder id first
      return (b.s3_folder || b.project_id).localeCompare(a.s3_folder || a.project_id);
    });

    const groups: { label: string; cards: S3ProjectCard[] }[] = [];
    let currentLabel = '';
    for (const card of sorted) {
      const when = card.last_file_at || card.landed_at || card.created_at;
      const dateLabel = when
        ? new Date(when).toLocaleDateString('en-GB', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
        : 'Unknown date';
      if (dateLabel !== currentLabel) {
        currentLabel = dateLabel;
        groups.push({ label: dateLabel, cards: [] });
      }
      groups[groups.length - 1].cards.push(card);
    }
    return groups;
  }, [cards]);

  const fmtTime = (iso: string | null | undefined) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // Projects whose annotator breakdown is open.
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpenIds((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="project-table-wrap">
      {grouped.map(group => (
        <div key={group.label} className="project-table-group">
          <div className="project-table-date">{group.label}</div>
          <table className="project-table">
            {/* One fixed set of widths for every date group, so the columns
                line up from group to group (each group is its own table). */}
            <colgroup>
              {TABLE_COLUMNS.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
              <tr>
                <th>Status</th>
                <th>S3 Folder</th>
                <th>Project Name</th>
                <th>Org</th>
                <th>Type</th>
                <th className="num">Files</th>
                <th>Landed</th>
                <th>Annotation</th>
                <th>Annotators</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {group.cards.map(card => {
                const st = card.running
                  ? { label: 'Running', cls: 'in-progress' }
                  : (STATUS_LABELS[card.status] || STATUS_LABELS.new);
                const open = openIds.has(card.project_id);
                const people = card.annotators || [];
                return (
                  <Fragment key={card.project_id}>
                  <tr
                    className={[
                      'project-table-row',
                      loading ? 'disabled' : '',
                      card.annotation_open ? 'open-annotation' : '',
                      card.new_files?.length ? 'has-new-files' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => !loading && onCardClick(card)}
                  >
                    <td>
                      <span className={`project-table-status ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="project-table-folder" title={card.s3_uri}>
                      {card.s3_folder || card.project_id}
                    </td>
                    <td className="project-table-name" title={[card.name, card.description].filter(Boolean).join('\n')}>
                      <span>{card.metadata_loaded ? card.name : `Project ${card.s3_folder || card.project_id}`}</span>
                      {card.new_files && card.new_files.length > 0 && (
                        <span
                          className="project-table-newfiles"
                          title={`Not analyzed yet:\n${card.new_files.join('\n')}`}
                        >
                          +{card.new_files.length} new file{card.new_files.length === 1 ? '' : 's'}
                          {card.latest_file_at && <> · added {fmtAgoFine(card.latest_file_at)}</>}
                        </span>
                      )}
                      {card.description && (
                        <span className="project-table-desc">{card.description}</span>
                      )}
                    </td>
                    <td className="project-table-org" title={card.organization || ''}>
                      {card.organization || '—'}
                    </td>
                    <td className="project-table-type" title={card.project_type || ""}>{card.project_type || "-"}</td>
                    <td className="num" title={card.upload_count ? `${card.upload_count} upload${card.upload_count === 1 ? '' : 's'} in Monco` : ''}>
                      {card.file_count || '-'}
                    </td>
                    <td
                      className="project-table-time"
                      title={[
                        `Landed ${new Date(card.landed_at || card.created_at || '').toLocaleString('en-GB')}`,
                        card.last_upload_at ? `Last upload ${new Date(card.last_upload_at).toLocaleString('en-GB')}` : '',
                        card.upload_count ? `${card.upload_count} upload${card.upload_count === 1 ? '' : 's'}` : '',
                        card.monco_status ? `Monco status: ${MONCO_STATUS[card.monco_status] || card.monco_status}` : '',
                      ].filter(Boolean).join('\n')}
                    >
                      <div>{fmtLanded(card.landed_at || card.created_at, card.last_file_at)}</div>
                      <div style={{ fontSize: 11 }}>
                        {[
                          card.last_file_at && card.last_file_at !== (card.landed_at || card.created_at) ? `↑ ${fmtAgoFine(card.last_file_at)}` : '',
                          card.monco_status ? (MONCO_STATUS[card.monco_status] || card.monco_status) : '',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td
                      onClick={(e) => { if (people.length) { e.stopPropagation(); toggle(card.project_id); } }}
                      style={{ cursor: people.length ? 'pointer' : undefined }}
                    >
                      <AssignmentBar counts={card.assignments} />
                    </td>
                    <td><AnnotatorToggle people={people} open={open} onToggle={() => toggle(card.project_id)} /></td>
                    <td className="project-table-actions">
                      {card.running ? (
                        // A run is in progress — offer to open and watch it,
                        // never a second Run (the backend would 409 anyway).
                        <button
                          className="project-table-btn"
                          onClick={e => { e.stopPropagation(); onCardClick(card); }}
                          disabled={loading}
                        >
                          Open &rarr;
                        </button>
                      ) : (
                        <>
                          {isOwner && card.new_files && card.new_files.length > 0 && (
                            <button
                              className="project-table-btn project-table-btn-new"
                              onClick={e => onProcessNewFiles(e, card)}
                              disabled={loading}
                              title={`Analyze new files (merged, non-destructive):\n${card.new_files.join('\n')}`}
                            >
                              Analyze {card.new_files.length} new {card.new_files.length === 1 ? 'file' : 'files'}
                            </button>
                          )}
                          {isOwner && (
                            <button
                              className="project-table-btn"
                              onClick={e => onRunSurvey(e, card)}
                              disabled={loading}
                            >
                              {card.has_agent_output ? 'Re-run' : 'Run'}
                            </button>
                          )}
                          {card.has_agent_output && (
                            <span className="project-table-link">Results &rarr;</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr className="project-table-detail">
                      <td colSpan={10} style={{ background: 'var(--bg-tertiary)' }}>
                        <AnnotatorDetail people={people} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
